'use client'
import { type ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTierComparison } from '@/features/tierComparison/TierComparisonProvider'
import { tierComparisonToTable } from '@/features/tierComparison/tierComparisonToTable'
import { DesktopComparisonTable } from 'ui/src/components/ComparisonTable/DesktopComparisonTable'
import { MobileComparisonTable } from 'ui/src/components/ComparisonTable/MobileComparisonTable'
import { Switch } from 'ui/src/components/Switch/Switch'
import { yStack } from 'ui/src/patterns/stack'
import { desktopOnly, mobileOnly } from 'ui/src/utils/visibility.css'
import { coverageSwitch, coverageSwitchLabel, header, tableWrapper } from './ProductTierComparison.css'

export type ProductTierComparisonProps = {
  coverageTextLabel: string
  title?: ReactNode
  subTitle?: ReactNode
  caption?: string
}

export function ProductTierComparison({ title, subTitle, coverageTextLabel, caption }: ProductTierComparisonProps) {
  const { t } = useTranslation()

  const [isCoverageTextVisible, toggleCoverageText] = useState(false)
  const tierComparison = useTierComparison()
  const { table } = tierComparisonToTable(tierComparison, {
    t,
    showCoverageText: isCoverageTextVisible,
    showPerilColor: true,
    showFirstColumnHeading: true,
  })

  const showCoverageSwitch = useMemo(
    () => tierComparison.rows.some((row) => row.cells.some((cell) => cell.coverageText != null)),
    [tierComparison],
  )

  return (
    <div className={yStack({ gap: 'lg' })}>
      <div className={header}>
        <div>
          {title}
          {subTitle}
        </div>

        {showCoverageSwitch && (
          <div className={coverageSwitch}>
            <label htmlFor="coverage-text" className={coverageSwitchLabel}>
              {coverageTextLabel}
            </label>
            <Switch checked={isCoverageTextVisible} onCheckedChange={toggleCoverageText} id="coverage-text" />
          </div>
        )}
      </div>

      <div className={tableWrapper}>
        {/* Just being safe here. It's expected for tier comparison tables to have at least 3 columns. */}
        {table.head.length > 2 ? (
          <>
            {/* Both tables are rendered in the HTML simultaneously and toggled via CSS media queries.
            This avoids CLS and keeps table data visible to search engines, at the cost of a
            slightly larger HTML payload. */}
            <MobileComparisonTable
              className={mobileOnly}
              {...table}
              caption={caption}
              defaultSelectedColumn={getDefaultSelectedColumn(tierComparison)}
            />
            <DesktopComparisonTable
              className={desktopOnly}
              {...table}
              caption={caption}
              headerSize={{ _: 'lg', lg: 'xl' }}
              isFirstHeaderFeatured={true}
            />
          </>
        ) : (
          <DesktopComparisonTable
            {...table}
            caption={caption}
            headerSize={{ _: 'lg', lg: 'xl' }}
            isFirstHeaderFeatured={true}
          />
        )}
      </div>
    </div>
  )
}

type TierComparison = ReturnType<typeof useTierComparison>
type TierComparisonProductVariant = TierComparison['variantColumns'][0]

function getDefaultSelectedColumn(tierComparison: TierComparison) {
  // A reasonable assumption here: 'typeOfContract' is the same for all 'variantColumns'
  const product = getProductByTypeOfContract(tierComparison.variantColumns[0].typeOfContract)
  let defaultTier: TierComparisonProductVariant | undefined

  switch (product) {
    case 'RENT':
    case 'BRF':
    case 'HOUSE':
      defaultTier = tierComparison.variantColumns.find((col) => col.termsVersion.includes('MAX'))
      break
    case 'STUDENT':
      defaultTier = tierComparison.variantColumns.find((col) => col.termsVersion.includes('RENT'))
      break
    case 'CAR':
      defaultTier = tierComparison.variantColumns.find((col) => col.termsVersion.includes('FULL'))
      break
    case 'PET':
      defaultTier = tierComparison.variantColumns.find((col) => col.termsVersion.includes('PREMIUM'))
      break
    default:
      break
  }

  return defaultTier?.displayNameSubtype
}

function getProductByTypeOfContract(typeOfContract: string) {
  if (typeOfContract.includes('SE_APARTMENT_RENT')) {
    return 'RENT'
  } else if (typeOfContract.includes('SE_APARTMENT_BRF')) {
    return 'BRF'
  } else if (typeOfContract.includes('SE_HOUSE')) {
    return 'HOUSE'
  } else if (typeOfContract.includes('SE_APARTMENT_STUDENT')) {
    return 'STUDENT'
  } else if (typeOfContract.includes('SE_CAR')) {
    return 'CAR'
  } else if (typeOfContract.includes('SE_DOG') || typeOfContract.includes('SE_CAT')) {
    return 'PET'
  }

  return 'OTHER'
}
